import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavBar } from "@/components/NavBar";
import { api, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StreamPlayer } from "@/components/StreamPlayer";
import { toast } from "sonner";
import {
  Radio, Coins, Eye, Volume2, VolumeX, Maximize2, Minimize2, MonitorPlay,
} from "lucide-react";

const PLATFORM_LABEL = { twitch: "Twitch", youtube: "YouTube", kick: "Kick" };

const IconBtn = ({ active, title, testId, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={testId}
    className={`border p-2 backdrop-blur transition-colors ${
      active
        ? "border-primary bg-primary/20 text-primary"
        : "border-border/60 bg-background/70 text-muted-foreground hover:text-foreground"
    }`}
  >
    {children}
  </button>
);

const Badge = ({ tone = "muted", children, testId }) => (
  <span
    data-testid={testId}
    className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${
      tone === "live"
        ? "bg-red-500 text-white"
        : tone === "earning"
          ? "border border-primary bg-background/80 text-primary"
          : "border border-border/60 bg-background/80 text-muted-foreground"
    }`}
  >
    {children}
  </span>
);

// the one stream that actually plays
const MainStage = ({ s, earning, muted, paused, theatre, onSound, onTheatre, onPlaying,
                     onOffline }) => (
  <div
    className={`border bg-card ${theatre ? "fixed inset-0 z-50 flex flex-col border-0" : "relative border-primary/60"}`}
    data-testid={`stream-tile-${s.id}`}
  >
    <div className={`relative w-full bg-black ${theatre ? "flex-1" : "aspect-video"}`}>
      <StreamPlayer
        s={s}
        muted={muted}
        onPlaying={(playing) => onPlaying(s.id, playing)}
        onOffline={() => onOffline(s)}
      />

      <div className="pointer-events-none absolute left-3 top-3 z-10 flex items-center gap-2">
        <Badge tone="live">Live</Badge>
        {earning ? (
          <Badge tone="earning" testId={`earning-badge-${s.id}`}>
            Earning
          </Badge>
        ) : (
          <Badge testId={`paused-badge-${s.id}`}>{paused ? "Paused" : "Not earning"}</Badge>
        )}
      </div>

      <div className="absolute right-3 top-3 z-10 flex items-center gap-2">
        <IconBtn active={!muted} testId={`sound-toggle-${s.id}`} onClick={() => onSound(s.id)}
          title={muted ? "Unmute" : "Mute"}>
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </IconBtn>
        <IconBtn active={theatre} testId={`theatre-toggle-${s.id}`} onClick={() => onTheatre(s.id)}
          title={theatre ? "Exit theatre mode (Esc)" : "Theatre mode"}>
          {theatre ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </IconBtn>
      </div>
    </div>
  </div>
);

// every non-main tile: muted embed with overlay chrome. `fill` stretches it to the grid row
// (3-live side column) instead of keeping its own 16:9 box (2-up / 4-up grids).
const SideTile = ({ s, onSetMain, onOffline, fill = false }) => (
  <div
    className={`relative border border-border bg-card ${fill ? "min-h-0 lg:h-full" : ""}`}
    data-testid={`stream-tile-${s.id}`}
  >
    <div
      className={`relative w-full bg-black ${fill ? "aspect-video lg:aspect-auto lg:h-full" : "aspect-video"}`}
    >
      <StreamPlayer s={s} muted onPlaying={() => {}} onOffline={() => onOffline?.(s)} />

      <div className="pointer-events-none absolute left-3 top-3 z-10 flex items-center gap-2">
        <Badge tone="live">Live</Badge>
        <Badge>Muted</Badge>
      </div>

      <button
        type="button"
        onClick={() => onSetMain(s.id)}
        data-testid={`set-as-main-${s.id}`}
        className="absolute bottom-3 right-3 z-10 flex items-center gap-2 border border-primary bg-background/85 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-primary backdrop-blur transition-colors hover:bg-primary/20"
      >
        <MonitorPlay className="h-3.5 w-3.5" /> Set as main
      </button>

      <div className="pointer-events-none absolute bottom-3 left-3 z-10 flex min-w-0 items-center gap-2 border border-border/60 bg-background/80 px-2 py-1 backdrop-blur">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
        <span className="truncate text-[11px] font-medium">{s.creator_name}</span>
        <span className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
          {PLATFORM_LABEL[s.platform] || s.platform}
        </span>
      </div>
    </div>
  </div>
);

const blockReason = ({ capped, liveCount, paused, earnMuted, earnPaused, visible }) =>
  capped
    ? "Daily cap reached — points resume tomorrow"
    : liveCount === 0
      ? "Nobody live yet — points start when a creator goes live"
      : !visible
        ? "Points paused — come back to this tab to carry on"
        : earnPaused
          ? "Stream paused — press play to keep earning"
          : earnMuted
            ? "Muted — unmute to start earning again"
            : paused
              ? "Earning paused — reload to start again"
              : "Tap start to earn on this stream";

const EarnPanel = (p) => {
  const { cfg, secs, points, earning, main, wide } = p;
  const cap = cfg.daily_cap_minutes;
  return (
    <div className="border border-border bg-card p-4" data-testid="earn-bar">
      <div className={wide ? "flex flex-col gap-5 lg:flex-row lg:items-center" : ""}>
      <div className={`flex items-start justify-between gap-4 ${wide ? "lg:w-[340px] lg:shrink-0" : ""}`}>
        <div>
          {earning ? (
            <span className="flex items-center gap-2 border border-primary/50 bg-primary/10 px-3 py-1.5 text-xs text-primary"
              data-testid="earn-pill">
              <Coins className="h-3.5 w-3.5" /> Earning {cfg.points_per_minute} /min
            </span>
          ) : (
            <span className="flex items-center gap-2 border border-border px-3 py-1.5 text-xs text-muted-foreground"
              data-testid="earn-pill">
              <Eye className="h-3.5 w-3.5 shrink-0" /> {blockReason(p)}
            </span>
          )}
          {main && (
            <p className="mt-2 text-[11px] text-muted-foreground">on {main.creator_name}</p>
          )}
        </div>
        <div className="text-right" data-testid="earn-points-today">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Earned today
          </div>
          <div className="mono text-3xl font-semibold text-primary">{fmt(points || 0)}</div>
        </div>
      </div>

      <div className={wide ? "min-w-0 flex-1" : "mt-4"}>
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <span>Next point</span>
          <span className="mono" data-testid="earn-next-point">
            {60 - (secs % 60)}s
          </span>
        </div>
        <div className="mt-1.5 h-1.5 w-full bg-secondary">
          <div
            className="h-full bg-primary transition-[width] duration-1000 ease-linear"
            style={{ width: `${((secs % 60) / 60) * 100}%` }}
            data-testid="earn-minute-bar"
          />
        </div>

        {cap > 0 && (
          <>
            <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <span>Today</span>
              <span className="mono" data-testid="earn-cap-label">
                {Math.floor(secs / 60)} / {cap} min
              </span>
            </div>
            <div className="mt-1.5 h-1 w-full bg-secondary">
              <div className="h-full bg-primary/50"
                style={{ width: `${Math.min(100, (secs / (cap * 60)) * 100)}%` }}
                data-testid="earn-cap-bar" />
            </div>
          </>
        )}
      </div>

      <StartButton {...p} className={wide ? "mt-0 w-full lg:w-auto" : "mt-4 w-full"} />
      </div>
    </div>
  );
};

const StartButton = ({ earning, capped, liveCount, earnPaused, visible, onStart,
                       className = "mt-4 w-full" }) =>
  earning || capped || liveCount === 0 || earnPaused || !visible ? null : (
    <button
      type="button"
      onClick={onStart}
      data-testid="start-earning"
      className={`flex items-center justify-center gap-2 border border-primary bg-primary/15 px-4 py-2.5 text-sm font-semibold uppercase tracking-[0.12em] text-primary transition-colors hover:bg-primary/25 ${className}`}
    >
      <Coins className="h-4 w-4" /> Start earning
    </button>
  );

export default function Watch() {
  const { setUser } = useAuth();
  const [data, setData] = useState(null);
  const [mainId, setMainId] = useState(null);
  const [soundOn, setSoundOn] = useState(false);
  const [hushed, setHushed] = useState(false);
  const [prompt, setPrompt] = useState(false);
  const [paused, setPaused] = useState(false);
  const [secs, setSecs] = useState(0);
  const [playing, setPlayingState] = useState({});
  const [theatre, setTheatre] = useState(false);
  const [droppedIds, setDroppedIds] = useState([]);
  const [visible, setVisible] = useState(document.visibilityState === "visible");
  const lastPrompt = useRef(Date.now());

  const load = useCallback(async () => {
    const { data: d } = await api.get("/watch");
    setData(d);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 45000);
    return () => clearInterval(t);
  }, [load]);

  const cfg = data?.config;
  const me = data?.me;
  const tiles = useMemo(() => data?.streams || [], [data]);
  const liveTiles = useMemo(
    () => tiles.filter((s) => s.live && !droppedIds.includes(s.id)),
    [tiles, droppedIds]
  );
  const liveCount = liveTiles.length;
  const main = liveTiles.find((s) => s.id === mainId) || liveTiles[0] || null;
  const sides = liveTiles.filter((s) => s.id !== main?.id);
  // 3 live = main + stacked side column aligned to the main player; 2 and 4 live = equal tiles
  const sideColumn = liveCount === 3;
  const equalGrid = liveCount === 2 || liveCount === 4;
  // solo view: 87% width, centred — header, player and earn bar all share this box
  const soloBox = liveCount === 1 ? "lg:mx-auto lg:w-[87%]" : "";

  const capSecs = cfg?.daily_cap_minutes ? cfg.daily_cap_minutes * 60 : 0;
  const shownSecs = capSecs ? Math.min(secs, capSecs) : secs;
  const shownPoints = cfg ? Math.floor(shownSecs / 60) * cfg.points_per_minute : 0;
  const capped = !!me?.capped || (capSecs > 0 && shownSecs >= capSecs);
  const earnPaused = !!main && playing[main.id] === false;
  const earning = !!(
    cfg?.enabled && main && visible && !capped && !prompt && !paused && !hushed && !earnPaused
  );

  const beat = useCallback(async () => {
    if (document.visibilityState !== "visible" || !main) return;
    try {
      const { data: r } = await api.post("/watch/heartbeat", {
        stream_id: main.id,
        seconds: cfg.heartbeat_seconds,
      });
      setData((d) => (d ? { ...d, me: { ...d.me, ...r } } : d));
      if (r.points_gained > 0) setUser((u) => (u ? { ...u, points_balance: r.points_balance } : u));
      (r.quests_completed || []).forEach((q) =>
        toast.success(`Quest complete: ${q.title} (+${q.points} points)`)
      );
      if (r.capped) toast.success("Daily watch cap reached — come back tomorrow for more.");
    } catch {
      setPaused(true);
    }
  }, [main, cfg, setUser]);

  useEffect(() => {
    if (!earning) return;
    const t = setInterval(() => {
      if (Date.now() - lastPrompt.current >= cfg.presence_prompt_minutes * 60000) {
        setPrompt(true);
        return;
      }
      beat();
    }, cfg.heartbeat_seconds * 1000);
    return () => clearInterval(t);
  }, [earning, beat, cfg]);

  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    setSecs(me?.seconds_today || 0);
  }, [me?.seconds_today]);

  useEffect(() => {
    if (!earning) return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") setSecs((s) => s + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [earning]);

  useEffect(() => {
    if (!theatre) return;
    const onKey = (e) => e.key === "Escape" && setTheatre(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [theatre]);

  const setPlaying = useCallback((id, isPlaying) => {
    setPlayingState((cur) => (cur[id] === isPlaying ? cur : { ...cur, [id]: isPlaying }));
  }, []);

  const dropStream = useCallback((stream) => {
    setDroppedIds((cur) => {
      if (cur.includes(stream.id)) return cur;
      toast.message(`${stream.creator_name} just went offline`);
      return [...cur, stream.id];
    });
    setTheatre(false);
  }, []);

  const toggleSound = () => {
    setHushed(soundOn);      // muting by hand stops the counter; unmuting clears it
    setSoundOn(!soundOn);
  };

  const setAsMain = (id) => {
    setMainId(id);
    setHushed(false);
    setTheatre(false);
  };

  const earnProps = {
    cfg, secs: shownSecs, points: shownPoints, earning, capped, paused, liveCount, visible, main,
    earnMuted: hushed, earnPaused,
    onStart: () => {
      setHushed(false);
      setPaused(false);
      setPrompt(false);
      if (!mainId && liveTiles[0]) setMainId(liveTiles[0].id);
    },
  };

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="mx-auto max-w-[1500px] px-6 py-6" data-testid="watch-page">
        <div className={`flex flex-wrap items-end justify-between gap-4 ${soloBox}`}>
          <div>
            <h1 className="text-2xl font-bold sm:text-3xl">{cfg?.headline || "Watch & earn"}</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{cfg?.blurb}</p>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            {cfg && (
              <>
                <span className="mono" data-testid="watch-rate">
                  <span className="text-primary">{cfg.points_per_minute}</span> pts / min
                </span>
                {cfg.daily_cap_minutes > 0 && (
                  <span className="mono" data-testid="watch-cap">
                    {Math.max(0, cfg.daily_cap_minutes - Math.floor(shownSecs / 60))}m left today
                  </span>
                )}
                <span className="mono" data-testid="watch-today">
                  {Math.floor(shownSecs / 60)}m watched
                </span>
              </>
            )}
            <span className="flex items-center gap-1.5">
              <Radio className={`h-3.5 w-3.5 ${liveCount ? "text-red-500" : ""}`} /> {liveCount} live
            </span>
          </div>
        </div>

        {liveCount === 0 ? (
          <div className="mt-6 border border-border bg-card p-10 text-center" data-testid="watch-empty">
            <p className="text-sm text-muted-foreground">
              Nobody is live right now — points start the moment a creator goes live.
            </p>
          </div>
        ) : (
          <>
            <div
              className={`mt-4 grid gap-4 ${
                sideColumn
                  ? "lg:grid-cols-[minmax(0,1fr)_380px] lg:items-stretch"
                  : equalGrid
                    ? "sm:grid-cols-2"
                    : soloBox
              }`}
              data-testid="watch-grid"
            >
              {sideColumn ? (
                <>
                  <div className="min-w-0">
                    <MainStage
                      s={main}
                      earning={earning}
                      muted={!soundOn}
                      paused={earnPaused}
                      theatre={theatre}
                      onSound={toggleSound}
                      onTheatre={() => setTheatre((t) => !t)}
                      onPlaying={setPlaying}
                      onOffline={dropStream}
                    />
                  </div>
                  <div className="grid min-h-0 gap-4 lg:grid-rows-2" data-testid="watch-side-column">
                    {sides.map((s) => (
                      <SideTile key={s.id} s={s} fill onSetMain={setAsMain}
                        onOffline={dropStream} />
                    ))}
                  </div>
                </>
              ) : (
                liveTiles.map((s) =>
                  s.id === main?.id ? (
                    <MainStage
                      key={s.id}
                      s={s}
                      earning={earning}
                      muted={!soundOn}
                      paused={earnPaused}
                      theatre={theatre}
                      onSound={toggleSound}
                      onTheatre={() => setTheatre((t) => !t)}
                      onPlaying={setPlaying}
                      onOffline={dropStream}
                    />
                  ) : (
                    <SideTile key={s.id} s={s} onSetMain={setAsMain} onOffline={dropStream} />
                  )
                )
              )}
            </div>

            {cfg && (
              <div className={`mt-[10px] ${soloBox}`}>
                <EarnPanel {...earnProps} wide />
              </div>
            )}
          </>
        )}

      </main>

      {theatre && cfg && (
        <div className="fixed bottom-20 left-4 z-[60] w-60 border border-border bg-card/95 p-3 backdrop-blur"
          data-testid="theatre-hud">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Earned today
            </span>
            <span className="mono text-lg font-semibold text-primary">{fmt(shownPoints)}</span>
          </div>
          <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <span>Next point</span>
            <span className="mono">{60 - (shownSecs % 60)}s</span>
          </div>
          <div className="mt-1.5 h-1.5 w-full bg-secondary">
            <div className="h-full bg-primary transition-[width] duration-1000 ease-linear"
              style={{ width: `${((shownSecs % 60) / 60) * 100}%` }} />
          </div>
          <StartButton {...earnProps} className="mt-3 w-full" />
        </div>
      )}

      <Dialog open={prompt} onOpenChange={() => {}}>
        <DialogContent className="rounded-none border-border bg-card sm:max-w-sm"
          data-testid="presence-dialog">
          <DialogHeader>
            <DialogTitle>Still watching?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Confirm you're still here to keep banking points.
          </p>
          <Button
            className="rounded-none"
            data-testid="presence-confirm"
            onClick={() => {
              lastPrompt.current = Date.now();
              setPrompt(false);
              beat();
            }}
          >
            I'm still here
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
